import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  fetchAllContent,
  createContent,
  updateContent,
  deleteContent,
} from '@/lib/contentApi';

// Per-category form schema: defines fields rendered in the editor dialog
const CATEGORY_SCHEMAS = {
  companies: {
    label: 'Companies',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'role', label: 'Role', type: 'text', required: true },
      { key: 'url', label: 'Website URL', type: 'url', required: true },
      { key: 'description', label: 'Short Description', type: 'textarea' },
      { key: 'detail', label: 'Detail Paragraph', type: 'textarea' },
    ],
    preview: (d) => d.name,
    subline: (d) => d.role,
  },
  awards: {
    label: 'Awards',
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'presenter', label: 'Presented By', type: 'text' },
      { key: 'date', label: 'Date', type: 'text', placeholder: 'e.g. July 2023' },
      { key: 'image', label: 'Image URL', type: 'url' },
    ],
    preview: (d) => d.title,
    subline: (d) => [d.presenter, d.date].filter(Boolean).join(' · '),
  },
  featured_articles: {
    label: 'Featured Press',
    fields: [
      { key: 'title', label: 'Article Title', type: 'text', required: true },
      { key: 'source', label: 'Publication / Source', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'text', placeholder: 'Opinion, Feature, News…' },
      { key: 'url', label: 'Article URL', type: 'url', required: true },
    ],
    preview: (d) => d.title,
    subline: (d) => [d.source, d.type].filter(Boolean).join(' · '),
  },
  publications: {
    label: 'Publications',
    fields: [
      { key: 'title', label: 'Publication Title', type: 'text', required: true },
      { key: 'platform', label: 'Platform', type: 'text', placeholder: 'LinkedIn, Zenodo…' },
      { key: 'url', label: 'URL / DOI', type: 'url', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
    preview: (d) => d.title,
    subline: (d) => d.platform,
  },
  tv_appearances: {
    label: 'TV Appearances',
    fields: [
      { key: 'title', label: 'Programme Topic', type: 'text', required: true },
      { key: 'broadcaster', label: 'Broadcaster', type: 'text', required: true },
      { key: 'programme', label: 'Programme Name', type: 'text' },
      { key: 'format', label: 'Format', type: 'text' },
      { key: 'duration', label: 'Duration', type: 'text', placeholder: 'e.g. One hour' },
      { key: 'date', label: 'Broadcast Date', type: 'text' },
      { key: 'reach', label: 'Reach / Distribution', type: 'textarea' },
      { key: 'url', label: 'Recording URL', type: 'url' },
      { key: 'images', label: 'Image URLs (one per line)', type: 'list' },
    ],
    preview: (d) => d.title,
    subline: (d) => [d.broadcaster, d.date].filter(Boolean).join(' · '),
  },
  insights: {
    label: 'Insights',
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'slug', label: 'Slug (URL path)', type: 'text', required: true, placeholder: 'auto-generated from title if blank' },
      { key: 'cover_image', label: 'Cover Image URL', type: 'url' },
      { key: 'excerpt', label: 'Excerpt (1-2 sentences)', type: 'textarea' },
      { key: 'body', label: 'Body (Markdown supported)', type: 'textarea-large' },
      { key: 'author', label: 'Author', type: 'text', placeholder: 'Ogaba Joseph Attah' },
      { key: 'publish_date', label: 'Publish Date', type: 'text', placeholder: 'e.g. 12 Feb 2026' },
      { key: 'tags', label: 'Tags (one per line)', type: 'list' },
      { key: 'published', label: 'Published (true/false)', type: 'text', placeholder: 'true' },
    ],
    preview: (d) => d.title,
    subline: (d) => [d.publish_date, d.published === 'true' || d.published === true ? 'Published' : 'Draft'].filter(Boolean).join(' · '),
  },
};

const CATEGORY_SINGULAR = {
  companies: 'Company',
  awards: 'Award',
  featured_articles: 'Press Article',
  publications: 'Publication',
  tv_appearances: 'TV Appearance',
  insights: 'Insight',
};

const CATEGORY_ORDER = ['companies', 'awards', 'featured_articles', 'publications', 'tv_appearances', 'insights'];

function slugify(text) {
  return (text || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function emptyForm(category) {
  const out = {};
  for (const f of CATEGORY_SCHEMAS[category].fields) {
    out[f.key] = '';
  }
  return out;
}

function dataToForm(category, data) {
  const out = {};
  for (const f of CATEGORY_SCHEMAS[category].fields) {
    const v = data[f.key];
    if (f.type === 'list') {
      out[f.key] = Array.isArray(v) ? v.join('\n') : (v || '');
    } else if (typeof v === 'boolean') {
      out[f.key] = v ? 'true' : 'false';
    } else {
      out[f.key] = v ?? '';
    }
  }
  return out;
}

function formToData(category, form) {
  const out = {};
  for (const f of CATEGORY_SCHEMAS[category].fields) {
    if (f.type === 'list') {
      out[f.key] = (form[f.key] || '').split('\n').map((s) => s.trim()).filter(Boolean);
    } else {
      out[f.key] = form[f.key] || '';
    }
  }
  // Insights: auto-slug + boolean published
  if (category === 'insights') {
    if (!out.slug && out.title) out.slug = slugify(out.title);
    if (out.slug) out.slug = slugify(out.slug);
    const p = (out.published || '').toString().toLowerCase();
    out.published = p === 'true' || p === '1' || p === 'yes';
  }
  return out;
}

export default function ContentManager() {
  const { getAuthHeader } = useAuth();
  const token = (getAuthHeader()?.Authorization || '').replace('Bearer ', '');

  const [activeCategory, setActiveCategory] = useState('companies');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm('companies'));
  const [saving, setSaving] = useState(false);

  const load = async (category) => {
    setIsLoading(true);
    try {
      const data = await fetchAllContent(token, category);
      setItems(data);
    } catch (e) {
      toast.error(`Failed to load ${CATEGORY_SCHEMAS[category].label}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, token]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm(activeCategory));
    setEditorOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm(dataToForm(activeCategory, item.data));
    setEditorOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const data = formToData(activeCategory, form);
      if (editingItem) {
        await updateContent(token, editingItem.id, { data });
        toast.success('Item updated');
      } else {
        const nextOrder = items.length ? Math.max(...items.map((i) => i.order)) + 1 : 0;
        await createContent(token, activeCategory, data, nextOrder);
        toast.success('Item created');
      }
      setEditorOpen(false);
      await load(activeCategory);
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${CATEGORY_SCHEMAS[activeCategory].preview(item.data)}"?`)) return;
    try {
      await deleteContent(token, item.id);
      toast.success('Item deleted');
      await load(activeCategory);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const move = async (item, direction) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swapWith = direction === 'up' ? items[idx - 1] : items[idx + 1];
    if (!swapWith) return;
    try {
      await Promise.all([
        updateContent(token, item.id, { order: swapWith.order }),
        updateContent(token, swapWith.id, { order: item.order }),
      ]);
      await load(activeCategory);
    } catch (e) {
      toast.error('Reorder failed');
    }
  };

  const schema = CATEGORY_SCHEMAS[activeCategory];

  return (
    <div className="p-6" data-testid="admin-content-manager">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Content Manager</h1>
          <p className="text-sm text-muted-foreground">Manage homepage and media content without touching code.</p>
        </div>
        <Button onClick={openCreate} data-testid="content-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Add New {CATEGORY_SINGULAR[activeCategory]}
        </Button>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-3">
        {CATEGORY_ORDER.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeCategory === cat
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`content-tab-${cat}`}
          >
            {CATEGORY_SCHEMAS[cat].label}
          </button>
        ))}
      </div>

      {/* Items list */}
      {isLoading ? (
        <div className="text-muted-foreground p-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-muted-foreground p-12 text-center border border-dashed border-border rounded-xl">
          No items yet. Click "Add New" to create the first one.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-4 p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors"
              data-testid={`content-item-${idx}`}
            >
              {/* Thumb */}
              {(item.data.image || item.data.images?.[0]) ? (
                <img
                  src={item.data.image || item.data.images[0]}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0 bg-muted"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{schema.preview(item.data)}</p>
                <p className="text-sm text-muted-foreground truncate">{schema.subline(item.data)}</p>
                {item.data.url && (
                  <a
                    href={item.data.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline"
                  >
                    <LinkIcon className="h-3 w-3" /> {item.data.url}
                  </a>
                )}
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="icon" variant="ghost" onClick={() => move(item, 'up')} disabled={idx === 0} data-testid={`content-move-up-${idx}`}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => move(item, 'down')} disabled={idx === items.length - 1} data-testid={`content-move-down-${idx}`}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => openEdit(item)} data-testid={`content-edit-${idx}`}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(item)} className="text-destructive" data-testid={`content-delete-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="content-editor-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit' : 'Add'} {CATEGORY_SINGULAR[activeCategory]}
            </DialogTitle>
            <DialogDescription>
              Fields marked * are required. URLs should include https://.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {schema.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`field-${f.key}`}>
                  {f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
                {f.type === 'textarea' || f.type === 'list' || f.type === 'textarea-large' ? (
                  <Textarea
                    id={`field-${f.key}`}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    rows={f.type === 'textarea-large' ? 12 : f.type === 'list' ? 4 : 3}
                    className={f.type === 'textarea-large' ? 'font-mono text-sm' : ''}
                    data-testid={`field-${f.key}`}
                  />
                ) : (
                  <Input
                    id={`field-${f.key}`}
                    type={f.type}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="content-editor-cancel">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="content-editor-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
